import { useAppSelector } from "../../../redux/hooks";
import AsinProductCell from "../../merchandising/components/cells/AsinCell";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";
import { Flex } from "../../../../styled-system/jsx";
import { TrendCell } from "../../merchandising/components/cells/TrendCell";
import { formatNumber } from "../../../utils/formatNumber";
import { ProductCell } from "../../merchandising/components/cells/ProductCell";
import { css } from "../../../../styled-system/css";
import { type ProductQueryV4Row } from "../api/product/v4ProductReportByProduct";
import { type ProductGroupedQueryV4Row } from "../api/product/v4ProductReportByGrouping";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { type ProductReportGrouping } from "../../../zodios/common/productReportGroupingSchema";
import { useModuleEnabled } from "../../../redux/selectors/modules";

// Union type for both grouped and non-grouped rows
export type ProductReportRow = ProductQueryV4Row | ProductGroupedQueryV4Row;

const columnHelper = createColumnBuilder<ProductReportRow>();

export const useProductReportColumns = (context?: string, grouping?: ProductReportGrouping) => {
  const { t } = useTranslation(["perf_reports"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const standardMetricIds = useStandardEntityMetrics("product");

  const isGroupedView = grouping && grouping !== "product_id";

  // Get the accessor field based on grouping
  const titleAccessor =
    grouping === "parent_asin_defaulted" ? "parent_asin_defaulted"
    : grouping === "asin" ? "asin"
    : "product_title";

  const columns = useMemo(
    () => [
      columnHelper.accessor(titleAccessor, {
        id: "product_title",
        header: () => (
          <span className={css({ marginRight: "auto" })}>
            {grouping === "parent_asin_defaulted" ?
              t(($) => $.perf_reports["Parent ASIN"])
            : grouping === "asin" ?
              t(($) => $.perf_reports["ASIN"])
            : t(($) => $.perf_reports["Product"])}
          </span>
        ),
        enableHiding: false,
        minSize: 100,
        size: isGroupedView ? 200 : 500,
        cell: (props) => {
          // For grouped views, show image + ASIN with external link
          if (isGroupedView) {
            const asin =
              grouping === "parent_asin_defaulted" ? props.row.original.parent_asin_defaulted : props.row.original.asin;
            return asin ? <AsinProductCell asin={asin} /> : null;
          }

          // For product view, show full ProductCell
          // Safe cast: when grouping is product_id, all product fields are present
          const row = props.row.original as ProductQueryV4Row;
          return (
            <ProductCell
              original={{
                ...row,
                product_id: row.product_id.toString(),
                pricing_plan__id: 0,
                pricing_plan__status: row.pricing_plan_status,
                price_override_end: row.price_override_end ? row.price_override_end.slice(0, 10) : "",
                cellContext: "merchandising",
              }}
              linkToNewTab={context === "productSelector"}
              value={row.product_title}
              showInventoryBadge={false}
              className={css({ marginRight: "auto", overflowX: "hidden" })}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            label: isGroupedView ? t(($) => $.perf_reports["ASIN"]) : t(($) => $.perf_reports["Product Title"]),
          },
        },
      }),
      columnHelper.accessor("parent_asin", {
        id: "parent_asin",
        header: t(($) => $.perf_reports["Parent ASIN"]),
        // Hide when grouped by parent_asin (redundant) or when not on Amazon
        enableHiding: channel === "amazon" && grouping !== "parent_asin_defaulted",
        enableResizing: false,
        size: 110,
        cell: ({ getValue }) => {
          const asin = getValue();

          if (!asin) {
            return null;
          }

          return (
            <Flex gap={"small"} alignItems={"center"}>
              <span>{asin}</span>
              <span className={css({ paddingBottom: "xsmall" })}>
                <ExternalProductLink asin={asin} />
              </span>
            </Flex>
          );
        },
      }),
      // Invisible columns to support filtering (only available in product view)
      ...(isGroupedView ?
        []
      : [
          columnHelper.accessor((row) => row, {
            id: "inventory_status",
            header: t(($) => $.perf_reports["Inventory State"]),
            enableHiding: false,
            meta: {
              columnFilterOptions: {
                format: "select",
              },
            },
          }),

          columnHelper.accessor((row) => row, {
            id: "sales_state",
            header: t(($) => $.perf_reports["Sales State"]),
            enableHiding: false,
            meta: {
              columnFilterOptions: {
                format: "select",
              },
            },
          }),
          tagsEnhancedEnabled ?
            columnHelper.accessor("tags_enhanced", {
              id: "tags_enhanced.id",
              header: t(($) => $.perf_reports["Tags"]),
              enableHiding: false,
              meta: {
                columnFilterOptions: {
                  format: "multi-select",
                  label: t(($) => $.perf_reports["Tags"]),
                },
              },
            })
          : columnHelper.accessor((row) => row, {
              id: "tags",
              header: t(($) => $.perf_reports["Tags"]),
              enableHiding: false,
              meta: {
                columnFilterOptions: {
                  format: "multi-select",
                },
              },
            }),
          columnHelper.accessor((row) => row, {
            id: "variations",
            header: t(($) => $.perf_reports["Variants"]),
            enableHiding: false,
            meta: {
              columnFilterOptions: {
                format: "select",
              },
            },
          }),
          columnHelper.accessor("primary_variant", {
            id: "primary_variant",
            header: t(($) => $.perf_reports["Primary Variant"]),
            enableHiding: channel === "amazon",
            size: 120,
            cell: ({ getValue }) => {
              const value = getValue();
              if (value === null || value === undefined) return null;
              return value ? t(($) => $.perf_reports["Yes"]) : t(($) => $.perf_reports["No"]);
            },
            meta: {
              columnFilterOptions: {
                format: "single-select",
              },
            },
          }),
          columnHelper.accessor((row) => row, {
            id: "has_ads",
            header: t(($) => $.perf_reports["Advertising Status"]),
            enableHiding: false,
            meta: {
              columnFilterOptions: {
                format: "single-select",
              },
            },
          }),
          columnHelper.accessor("category_id", {
            id: "category_id",
            header: t(($) => $.perf_reports["Category"]),
            enableHiding: false,
            meta: {
              columnFilterOptions: {
                format: "select",
              },
            },
          }),
          columnHelper.accessor("pricing_plan_status", {
            id: "pricing_plan_status",
            header: t(($) => $.perf_reports["Enrollment State"]),
            enableHiding: false,
            meta: {
              columnFilterOptions: {
                format: "select",
              },
            },
          }),
        ]),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
      // Product-specific columns (only available in product view)
      ...(isGroupedView ?
        []
      : [
          columnHelper.accessor("product_quantity_fba", {
            id: "product_quantity_fba",
            header: t(($) => $.perf_reports["Product Quantity FBA"]),
            cell: ({ getValue }) => formatNumber(getValue()),
          }),
          columnHelper.accessor("product_quantity", {
            id: "product_quantity",
            header: t(($) => $.perf_reports["Product Quantity"]),
            cell: ({ getValue }) => formatNumber(getValue()),
          }),
          columnHelper.accessor("trends_score", {
            id: "trends_score",
            header: t(($) => $.perf_reports["Trends"]),
            enableResizing: false,
            size: 75,
            cell: (props) => {
              const { margin_trend, sales_trend, trends_score } = props.row.original;

              const trends = [];

              if (margin_trend && margin_trend >= 1) {
                trends.push(t(($) => $.perf_reports["Margin is trending up"]));
              }
              if (margin_trend && margin_trend <= -1) {
                trends.push(t(($) => $.perf_reports["Margin is trending down"]));
              }

              if (sales_trend && sales_trend >= 1) {
                trends.push(t(($) => $.perf_reports["Sales are trending up"]));
              }
              if (sales_trend && sales_trend <= -1) {
                trends.push(t(($) => $.perf_reports["Sales are trending down"]));
              }

              return <TrendCell value={trends_score} trends={trends} />;
            },
            meta: {
              columnFilterOptions: {
                format: "number",
              },
            },
          }),
        ]),
    ],
    [standardMetricIds, channel, context, grouping, isGroupedView, titleAccessor, tagsEnhancedEnabled, t],
  );
  return columns;
};
